// client/src/helpers/planCDismissal.ts
//
// TRIL-116: Plan C dismissal time-decay logic. Spec §366 sequence:
// session → 24h → 7d → recurring 7d. The session window is enforced
// in-memory via a flag; later windows use ISO-timestamp comparison
// against the persisted `dismissedAt`.

export interface NudgeState {
  dismissedAt: string | null; // ISO; null = never dismissed
  dismissCount: number; // 0 = never, ≥1 = N dismissals total
  optedOut: boolean;
}

interface ShouldShowOptions {
  sessionDismissedThisLoad: boolean; // in-memory flag, resets on reload
}

export const SESSION_WINDOW_SENTINEL = -1;

const DAY_MS = 24 * 60 * 60 * 1000;

export function nextDismissalWindowMs(dismissCount: number): number {
  if (dismissCount <= 1) return SESSION_WINDOW_SENTINEL;
  if (dismissCount === 2) return DAY_MS;
  return 7 * DAY_MS; // 3rd, 4th, ... all 7d
}

export function shouldShowNudge(
  state: NudgeState,
  nowMs: number,
  opts: ShouldShowOptions,
): boolean {
  if (state.optedOut) return false;
  if (state.dismissCount === 0) return true;

  // 1st dismiss → session-only (in-memory flag, no persistent suppression)
  if (state.dismissCount === 1) {
    return !opts.sessionDismissedThisLoad;
  }

  // 2nd+ → timestamp window
  if (!state.dismissedAt) return true;
  const dismissedAtMs = new Date(state.dismissedAt).getTime();
  const window = nextDismissalWindowMs(state.dismissCount);
  if (window === SESSION_WINDOW_SENTINEL) return !opts.sessionDismissedThisLoad;
  return nowMs - dismissedAtMs >= window;
}
